import { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import type { Instance, InstanceEvent, CreateInstanceDto } from '../types/instance';

// Global queries

export function useAllInstances(status?: string) {
  return useQuery<Instance[]>({
    queryKey: ['instances', 'all', { status }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (status) params.set('status', status);
      const res = await api.get(`/api/instances?${params.toString()}`);
      return res.data;
    },
  });
}

export function useActiveInstances() {
  return useQuery<Instance[]>({
    queryKey: ['instances', 'active'],
    queryFn: async () => {
      const res = await api.get('/api/instances/active');
      return res.data;
    },
    refetchInterval: 10000,
  });
}

// Project-scoped queries

export function useInstances(projectId: string | undefined) {
  return useQuery<Instance[]>({
    queryKey: ['projects', projectId, 'instances'],
    queryFn: async () => {
      const res = await api.get(`/api/projects/${projectId}/instances`);
      return res.data;
    },
    enabled: !!projectId,
  });
}

export function useInstance(id: string | undefined) {
  return useQuery<Instance>({
    queryKey: ['instances', id],
    queryFn: async () => {
      const res = await api.get(`/api/instances/${id}`);
      return res.data;
    },
    enabled: !!id,
  });
}

export function useInstanceEvents(id: string | undefined, limit?: number, offset?: number) {
  return useQuery<InstanceEvent[]>({
    queryKey: ['instances', id, 'events', { limit, offset }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (limit !== undefined) params.set('limit', String(limit));
      if (offset !== undefined) params.set('offset', String(offset));
      const res = await api.get(`/api/instances/${id}/events?${params.toString()}`);
      return res.data.events ?? res.data;
    },
    enabled: !!id,
  });
}

// Mutations

export function useCreateInstance(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateInstanceDto) => {
      const res = await api.post(`/api/projects/${projectId}/instances`, data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'instances'] });
    },
  });
}

export function useCreateGlobalInstance() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateInstanceDto) => {
      const res = await api.post('/api/instances', data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['instances'] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export function useSpawnInstance() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post(`/api/instances/${id}/spawn`);
      return res.data;
    },
    onSuccess: (data: Instance) => {
      queryClient.invalidateQueries({ queryKey: ['instances', data.id] });
      queryClient.invalidateQueries({ queryKey: ['projects', data.projectId, 'instances'] });
    },
  });
}

export function usePauseInstance() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post(`/api/instances/${id}/pause`);
      return res.data;
    },
    onSuccess: (data: Instance) => {
      queryClient.invalidateQueries({ queryKey: ['instances', data.id] });
      queryClient.invalidateQueries({ queryKey: ['projects', data.projectId, 'instances'] });
    },
  });
}

export function useResumeInstance() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, prompt }: { id: string; prompt?: string }) => {
      const res = await api.post(`/api/instances/${id}/resume`, { prompt });
      return res.data;
    },
    onSuccess: (data: Instance) => {
      queryClient.invalidateQueries({ queryKey: ['instances', data.id] });
      queryClient.invalidateQueries({ queryKey: ['projects', data.projectId, 'instances'] });
    },
  });
}

export function useStopInstance() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post(`/api/instances/${id}/stop`);
      return res.data;
    },
    onSuccess: (data: Instance) => {
      queryClient.invalidateQueries({ queryKey: ['instances', data.id] });
      queryClient.invalidateQueries({ queryKey: ['projects', data.projectId, 'instances'] });
    },
  });
}

export function useRelaunchInstance() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post(`/api/instances/${id}/relaunch`);
      return res.data as Instance;
    },
    onSuccess: (data: Instance) => {
      queryClient.invalidateQueries({ queryKey: ['projects', data.projectId, 'instances'] });
    },
  });
}

export function useDeleteInstance() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/instances/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

// Search

export function useSearchInstanceEvents(
  instanceId: string | undefined,
  query: string,
  eventType?: string,
) {
  return useQuery<InstanceEvent[]>({
    queryKey: ['instances', instanceId, 'events', 'search', { query, eventType }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (query) params.set('q', query);
      if (eventType) params.set('type', eventType);
      const res = await api.get(`/api/instances/${instanceId}/events/search?${params.toString()}`);
      return res.data;
    },
    enabled: !!instanceId && query.length > 0,
  });
}

// SSE Hook

export function useInstanceEventStream(instanceId: string | null) {
  const [events, setEvents] = useState<InstanceEvent[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const eventSourceRef = useRef<EventSource | null>(null);

  const disconnect = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    setIsConnected(false);
  }, []);

  useEffect(() => {
    if (!instanceId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      disconnect();
      setEvents([]);
      return;
    }

    const token = localStorage.getItem('token');
    const url = `/api/instances/${instanceId}/events/stream${token ? `?token=${encodeURIComponent(token)}` : ''}`;
    const es = new EventSource(url);
    eventSourceRef.current = es;

    es.onopen = () => {
      setIsConnected(true);
    };

    es.onmessage = (event) => {
      try {
        const parsed: InstanceEvent = JSON.parse(event.data);
        setEvents((prev) => [...prev, parsed]);
      } catch {
        // ignore malformed events
      }
    };

    es.onerror = () => {
      setIsConnected(false);
    };

    return () => {
      es.close();
      eventSourceRef.current = null;
      setIsConnected(false);
    };
  }, [instanceId, disconnect]);

  return { events, isConnected };
}
